/**
 * ColaboradoresView -- Manage colaboradores scoped to the selected business.
 * Includes status filter bar, inline create form, and delete per row.
 *
 * @module presentation/views/ColaboradoresView
 */

import { ColaboradorApiClient } from '../../infrastructure/api/ColaboradorApiClient.js';
import { getSelectedBusiness, getSelectedBusinessName } from '../components/BusinessSelector.js';
import { rutaDeLaFicha } from '../../application/personas/fichaUnica.js';
import { abrirModalDePin } from '../shared/NxModalDePin.js';
import { insigniaHtml, COLOR_ESTADO } from '../shared/insignia.js';
import { regresoHtml } from '../shared/regreso.js';
import { esc } from '../shared/texto.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

const ESTADO_LABELS = {
  activo: 'activo',
  inactivo: 'inactivo'
};

const ESTADO_COLORS = {
  activo: COLOR_ESTADO.ok,
  inactivo: COLOR_ESTADO.apagado
};

let currentPage = 1;
let currentFilter = 'all';

function badge(estado) {
  const color = ESTADO_COLORS[estado] || COLOR_ESTADO.apagado;
  const label = ESTADO_LABELS[estado] || estado || 'sin estado';
  return insigniaHtml(label, color);
}

function fmtDate(dt) {
  return formatearFecha(dt, { year: 'numeric', month: 'short', day: 'numeric' });
}

// COLABORADOR-EDITAR-NOMBRE-T1: lo que escribe el negocio (nombre, documento, telefono, cargo, area) pasa por esc.
function colaboradorRow(c) {
  const nombre = esc(c.nombreCompleto || c.nombre_completo || '(sin nombre)');
  const doc = c.documento ? `<span class="colab-doc">CC ${esc(c.documento)}</span>` : '';
  const tel = c.telefono ? `<span class="colab-tel">&#128222; ${esc(c.telefono)}</span>` : '';
  const ingreso = c.fechaIngreso ? `<span class="colab-date">Desde ${fmtDate(c.fechaIngreso)}</span>` : '';
  return `
    <div class="artifact-item colaborador-row" data-id="${c.id}">
      <div class="task-info">
        <div class="artifact-item-title">${nombre} ${doc}</div>
        <div class="artifact-item-meta">
          <span>${esc(c.cargo || '—')}</span>
          ${c.area ? `&middot; <span>${esc(c.area)}</span>` : ''}
          ${tel}
          ${ingreso}
        </div>
      </div>
      <div class="task-actions">
        ${badge(c.estado)}
        <button class="btn-icon btn-ficha-colab" data-id="${c.id}"
          title="Ver ficha del colaborador (asistencia, créditos, capacitación)">&#128203; Ficha</button>
        <button class="btn-icon btn-set-pin" data-id="${c.id}" data-nombre="${nombre}"
          title="Configurar PIN de asistencia (kiosk)">&#128273; PIN</button>
        <button class="btn-icon btn-edit-colab" data-id="${c.id}" title="Editar nombre, documento y teléfono">&#9998; Editar</button>
        <button class="btn-icon btn-toggle-estado" data-id="${c.id}" data-estado="${c.estado}" title="${c.estado === 'activo' ? 'Desactivar' : 'Activar'}">
          ${c.estado === 'activo' ? '&#128683;' : '&#9989;'}
        </button>
      </div>
    </div>`;
}

export async function renderColaboradores(container) {
  currentFilter = 'all';
  currentPage = 1;

  const businessId = getSelectedBusiness();

  if (!businessId || businessId === 'undefined') {
    container.innerHTML = `
      <section class="artifact-view">
        <div class="artifact-header">
          <div>
            ${regresoHtml()}
            <h1>Colaboradores</h1>
          </div>
        </div>
        <p class="empty-state">Selecciona un negocio primero desde el selector de negocios.</p>
      </section>`;
    return;
  }

  const businessName = getSelectedBusinessName() || businessId;

  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>Colaboradores</h1>
        </div>
        <button class="btn-primary btn-sm" id="btn-new-colab">+ Nuevo colaborador</button>
      </div>

      <div class="business-scope-banner">
        <span class="business-scope-icon">&#127970;</span>
        <span class="business-scope-label">Negocio activo:</span>
        <span class="business-scope-name" id="nx-colab-business-name">${businessName}</span>
        <span class="business-scope-hint">Los datos mostrados corresponden solo a este negocio</span>
      </div>

      <div class="task-filter-bar" id="nx-colab-filters">
        <button class="btn-filter active" data-filter="all">Todos</button>
        <button class="btn-filter" data-filter="activo">Activos</button>
        <button class="btn-filter" data-filter="inactivo">Inactivos</button>
      </div>

      <div id="nx-colab-form-area" hidden>
        <form id="nx-colab-form" class="inline-form inline-form-col">
          <div class="form-row">
            <input type="text" id="nx-colab-nombre" placeholder="Nombre completo *" required class="form-input">
            <input type="text" id="nx-colab-documento" placeholder="Cedula / Documento" class="form-input form-input-sm">
            <input type="text" id="nx-colab-telefono" placeholder="Telefono" class="form-input form-input-sm">
          </div>
          <div class="form-row">
            <input type="text" id="nx-colab-cargo" placeholder="Cargo" class="form-input form-input-sm">
            <input type="text" id="nx-colab-area" placeholder="Area" class="form-input form-input-sm">
            <input type="date" id="nx-colab-ingreso" class="form-input form-input-sm" title="Fecha de ingreso">
          </div>
          <div class="form-row">
            <button type="submit" class="btn-primary btn-sm" id="btn-submit-colab">Crear</button>
            <button type="button" class="btn-ghost btn-sm" id="btn-cancel-colab">Cancelar</button>
          </div>
        </form>
        <p id="nx-colab-form-error" class="auth-error" hidden></p>
      </div>

      <div class="artifact-list" id="nx-colab-list">
        <p class="empty-state">Cargando colaboradores...</p>
      </div>
      <div id="nx-colab-pager" class="pager"></div>
    </section>`;

  setupFilterHandlers();
  setupFormHandlers(businessId);
  setupListHandlers(businessId);
  await loadPage(businessId, currentPage);

  // Recargar cuando cambie el negocio seleccionado
  const onBusinessChange = (e) => {
    const newId = e.detail?.businessId || getSelectedBusiness();
    if (!newId || newId === 'undefined') return;
    const newName = e.detail?.businessName || getSelectedBusinessName() || newId;
    currentFilter = 'all';
    currentPage = 1;
    const $nameEl = document.getElementById('nx-colab-business-name');
    if ($nameEl) $nameEl.textContent = newName;
    loadPage(newId, 1);
  };
  window.addEventListener('business:changed', onBusinessChange);
  // Limpiar listener cuando la vista sea reemplazada
  container._cleanupBusinessListener = () =>
    window.removeEventListener('business:changed', onBusinessChange);
}

function setupFilterHandlers() {
  const $bar = document.getElementById('nx-colab-filters');
  $bar.addEventListener('click', (e) => {
    const btn = e.target.closest('.btn-filter');
    if (!btn) return;
    $bar.querySelectorAll('.btn-filter').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    currentFilter = btn.dataset.filter;
    currentPage = 1;
    loadPage(getSelectedBusiness(), 1);
  });
}

function setupFormHandlers(businessId) {
  const $btn = document.getElementById('btn-new-colab');
  const $area = document.getElementById('nx-colab-form-area');
  const $form = document.getElementById('nx-colab-form');
  const $cancel = document.getElementById('btn-cancel-colab');
  const $error = document.getElementById('nx-colab-form-error');

  $btn.onclick = () => {
    $area.hidden = !$area.hidden;
    if (!$area.hidden) document.getElementById('nx-colab-nombre').focus();
  };

  $cancel.onclick = () => { $area.hidden = true; };

  $form.onsubmit = async (e) => {
    e.preventDefault();
    $error.hidden = true;

    const editId = $form.dataset.editId;
    const nombreCompleto = document.getElementById('nx-colab-nombre').value.trim();
    const documento = document.getElementById('nx-colab-documento').value.trim();
    const telefono = document.getElementById('nx-colab-telefono').value.trim();
    const cargo = document.getElementById('nx-colab-cargo').value.trim();
    const area = document.getElementById('nx-colab-area').value.trim();
    const fechaIngreso = document.getElementById('nx-colab-ingreso').value || null;

    if (!nombreCompleto) {
      $error.textContent = 'Nombre completo es requerido';
      $error.hidden = false;
      return;
    }

    const payload = { nombreCompleto, documento, telefono, cargo, area, fechaIngreso };

    const result = editId
      ? await ColaboradorApiClient.update(businessId, editId, payload)
      : await ColaboradorApiClient.create(businessId, payload);

    if (result.ok) {
      $area.hidden = true;
      $form.reset();
      delete $form.dataset.editId;
      document.getElementById('btn-submit-colab').textContent = 'Crear';
      currentPage = 1;
      await loadPage(businessId, 1);
    } else {
      $error.textContent = result.error || 'Error al guardar';
      $error.hidden = false;
    }
  };
}

function setupListHandlers(businessId) {
  const $list = document.getElementById('nx-colab-list');

  $list.addEventListener('click', async (e) => {
    // FICHA-COLAB-P-T1 (bit #5641): la ficha del colaborador para el DUENO.
    // AUDIENCIA: PROPIETARIO. SUJ-T8: abre la ficha unica de la persona (#/persona/colaborador/{id}).
    const fichaBtn = e.target.closest('.btn-ficha-colab');
    if (fichaBtn) {
      const id = fichaBtn.dataset.id;
      // SUJ-T8 (#17380): la ficha es UNA y se abre igual desde Personas, Colaboradores y Usuarios.
      // Antes era un panel lateral propio; ahora lleva a la ficha unica de la persona de esta ficha.
      window.location.hash = rutaDeLaFicha('colaborador', id);
      return;
    }

    // Editar
    const editBtn = e.target.closest('.btn-edit-colab');
    if (editBtn) {
      const id = editBtn.dataset.id;
      const res = await ColaboradorApiClient.getById(businessId, id);
      if (!res.ok) return;
      const c = res.data?.data || res.data;
      const $area = document.getElementById('nx-colab-form-area');
      const $form = document.getElementById('nx-colab-form');
      $form.dataset.editId = id;
      document.getElementById('nx-colab-nombre').value = c.nombreCompleto || '';
      document.getElementById('nx-colab-documento').value = c.documento || '';
      document.getElementById('nx-colab-telefono').value = c.telefono || '';
      document.getElementById('nx-colab-cargo').value = c.cargo || '';
      document.getElementById('nx-colab-area').value = c.area || '';
      document.getElementById('nx-colab-ingreso').value = c.fechaIngreso ? c.fechaIngreso.substring(0, 10) : '';
      document.getElementById('btn-submit-colab').textContent = 'Guardar';
      $area.hidden = false;
      document.getElementById('nx-colab-nombre').focus();
      return;
    }

    // Configurar PIN de asistencia
    const pinBtn = e.target.closest('.btn-set-pin');
    if (pinBtn) {
      const id = pinBtn.dataset.id;
      const nombre = pinBtn.dataset.nombre;
      abrirModalDePin({ businessId, colaboradorId: id, nombre, negocio: getSelectedBusinessName() });
      return;
    }

    // Cambiar estado
    const toggleBtn = e.target.closest('.btn-toggle-estado');
    if (toggleBtn) {
      const id = toggleBtn.dataset.id;
      const estadoActual = toggleBtn.dataset.estado;
      const nuevoEstado = estadoActual === 'activo' ? 'inactivo' : 'activo';
      toggleBtn.disabled = true;
      const result = await ColaboradorApiClient.update(businessId, id, { estado: nuevoEstado });
      if (result.ok) {
        await loadPage(businessId, currentPage);
      } else {
        toggleBtn.disabled = false;
      }
      return;
    }
  });
}

async function loadPage(businessId, page) {
  const $list = document.getElementById('nx-colab-list');
  const $pager = document.getElementById('nx-colab-pager');
  if (!$list) return;

  $list.innerHTML = '<p class="empty-state">Cargando...</p>';
  currentPage = page;

  const estado = currentFilter !== 'all' ? currentFilter : '';
  const result = await ColaboradorApiClient.list(businessId, page, 50, estado);

  if (!result.ok) {
    $list.innerHTML = `<p class="empty-state">Error: ${result.error}</p>`;
    return;
  }

  const payload = result.data?.data || result.data;
  const items = payload?.items || payload || [];

  const filterLabel = currentFilter === 'all'
    ? '' : ` (${ESTADO_LABELS[currentFilter] || currentFilter})`;

  if (items.length === 0) {
    $list.innerHTML = `<p class="empty-state">No hay colaboradores${filterLabel}</p>`;
    $pager.innerHTML = '';
    return;
  }

  $list.innerHTML = items.map(colaboradorRow).join('');

  const totalPages = payload?.totalPages || 1;
  const pg = payload?.page || page;

  if (totalPages > 1) {
    let html = '';
    for (let p = 1; p <= totalPages; p++) {
      const cls = p === pg ? 'btn-primary' : 'btn-ghost';
      html += `<button class="${cls} btn-sm page-btn" data-page="${p}">${p}</button>`;
    }
    $pager.innerHTML = html;
    $pager.querySelectorAll('.page-btn').forEach(btn => {
      btn.onclick = () => loadPage(businessId, Number(btn.dataset.page));
    });
  } else {
    $pager.innerHTML = '';
  }
}
